import { get } from 'svelte/store';

/**
 * Orchestrate loading a historical backtest run into the shared stores (TASK-20260728), extracted from
 * Backtesting.svelte so the instant-selection + stale-response sequencing is unit-testable.
 *
 * Contract:
 *  - SYNCHRONOUSLY (before the first await): clear the prior result, mark the row selected, and set the
 *    loading overlay from the row summary — so the list highlight + header/skeletons appear immediately.
 *  - Then fetch the heavy result. Apply it ONLY if this run is still the selected one when the response
 *    lands (stale-response guard: a slower earlier load must not clobber a newer selection).
 *  - On error, clear the loading overlay and surface the message (also guarded by the current selection).
 *
 * @param {{ runId: string, summary?: object|null, orgId: string|null }} sel
 * @param {{
 *   stores: { backtestResult, backtestError, selectedRunId, loadingRun },
 *   getRun: (runId: string, orgId: string|null) => Promise<any>,
 *   onResult?: (result: any) => void,
 * }} deps
 */
export async function loadRunInto({ runId, summary = null, orgId = null }, deps) {
  const { backtestResult, backtestError, selectedRunId, loadingRun } = deps.stores;

  // Instant feedback — no await before these.
  backtestError.set(null);
  backtestResult.set(null);
  selectedRunId.set(runId);
  loadingRun.set(summary ?? { runId });

  try {
    const result = await deps.getRun(runId, orgId);
    if (get(selectedRunId) !== runId) return; // superseded by a newer click — drop this stale response
    loadingRun.set(null);
    if (!result) {
      // A vanished/unreadable run (404/empty) resolves to null — surface it, don't silently drop back to the
      // config form with no feedback (the loading overlay would otherwise just disappear).
      backtestError.set('Could not load that run.');
      return;
    }
    backtestResult.set(result);
    deps.onResult?.(result);
  } catch (err) {
    if (get(selectedRunId) !== runId) return;
    loadingRun.set(null);
    backtestError.set('Could not load that run.');
  }
}
